// @ts-check
import React from 'react';
import PropTypes from 'prop-types';

import { TestableCard, TestableText } from '@components/general/TestableComponents';

import { formatCurrency, formatList, getRagMeta } from '../shared/businessViewConfig';
import { EXPOSURE_DAYS } from './businessViewDetailMapper';

/**
 * One of the three figures along the middle of the card.
 * @param {{ testKey: string, label: string, value: React.ReactNode }} props
 */
const ImpactFigure = ({ testKey, label, value }) => (
  <div className="min-w-0 flex-1 px-6 py-1">
    <TestableText dataTestId={`bv-flow-impact-${testKey}`} variant="h4" className="normal-case text-blue-500">
      {value}
    </TestableText>
    <TestableText
      dataTestId={`bv-flow-impact-label-${testKey}`}
      className="mt-1 uppercase tracking-wide text-gray-700 dark:text-gray-300"
    >
      {label}
    </TestableText>
  </div>
);

ImpactFigure.propTypes = {
  testKey: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

/**
 * Sentence describing what the flow's status is costing.
 *
 * Says "non-passing" rather than "failing" because the count includes skipped, not executed
 * and not found tests, none of which failed.
 *
 * Whether the cost is stated as being lost or merely at stake keys off hasCurrentRisk, not
 * the test counts: a healthy flow with skipped tests is a normal run, and is losing nothing.
 *
 * @param {{ statusPhrase: string, nonPassingCount: number, dailyCost?: number|null, hasCurrentRisk: boolean, categories: string[] }} props
 */
const ImpactNarrative = ({ statusPhrase, nonPassingCount, dailyCost, hasCurrentRisk, categories }) => {
  return (
    <TestableText dataTestId="bv-flow-impact-narrative" variant="paragraph" className="text-gray-700 dark:text-gray-300">
      This flow is <span className="font-semibold">{statusPhrase}</span> with{' '}
      <span className="font-semibold">{nonPassingCount}</span> non-passing{' '}
      {nonPassingCount === 1 ? 'test' : 'tests'}
      {dailyCost != null && (
        <>
          {hasCurrentRisk ? ', exposing an estimated ' : '. A failure could expose an estimated '}
          <span className="font-semibold">{formatCurrency(dailyCost)}/day</span> in revenue or SLA
          impact
        </>
      )}
      {categories.length > 0 && (
        <>
          {' '}
          across {formatList(categories.map((category) => category.toLowerCase()))}{' '}
          {categories.length === 1 ? 'category' : 'categories'}
        </>
      )}
      .
    </TestableText>
  );
};

ImpactNarrative.propTypes = {
  statusPhrase: PropTypes.string.isRequired,
  nonPassingCount: PropTypes.number.isRequired,
  dailyCost: PropTypes.number,
  hasCurrentRisk: PropTypes.bool,
  categories: PropTypes.arrayOf(PropTypes.string).isRequired,
};

/**
 * Business impact card: what the flow's status means in money, projected over a week and a
 * month, its rank against the project's other flows, and its risk categories.
 *
 * The figures are a daily cost projected forward, not measured losses - hence "exposure",
 * and hence a healthy flow still having them. See resolveDailyCost for which cost.
 *
 * @param {Object} props
 * @param {import('../shared/businessView.types').BusinessViewWorkflowDetail} props.workflow
 * @param {number} props.nonPassingCount
 * @param {number|null} [props.dailyCost]
 * @param {boolean} [props.hasCurrentRisk]
 * @param {number|null} props.exposure7Day
 * @param {number|null} props.exposure30Day
 * @returns {JSX.Element}
 */
const BusinessViewFlowImpactCard = ({
  workflow,
  nonPassingCount,
  dailyCost,
  hasCurrentRisk = false,
  exposure7Day,
  exposure30Day,
}) => {
  const { riskStatus, riskCategories = [], costImpactRank, portfolioFlowCount } = workflow || {};

  const { phrase: statusPhrase } = getRagMeta(riskStatus || 'unavailable');

  // p-0 because this card manages its own padding: the rule has to span its full width.
  return (
    <TestableCard dataTestId="bv-flow-impact-card" className="flex h-full flex-col overflow-hidden p-0 shadow-sm">
      {/*
        flex-1 is what makes justify-between do anything: in a flex column a child is sized to
        its content, so on its own there is no free space to distribute. Growing this section
        hands it the card spare height, and leaves the rule and the categories pinned to the
        bottom where a divider belongs.
      */}
      <div className="flex flex-1 flex-col justify-between p-5">
        <TestableText dataTestId="bv-flow-impact-title" variant="h4" className="uppercase text-blue-500">
          Business Impact
        </TestableText>

        <div className="mt-3">
          <ImpactNarrative
            statusPhrase={statusPhrase}
            nonPassingCount={nonPassingCount}
            dailyCost={dailyCost}
            hasCurrentRisk={hasCurrentRisk}
            categories={riskCategories}
          />
        </div>

        <div className="mt-6 flex flex-col divide-y divide-gray-300 sm:flex-row sm:divide-x sm:divide-y-0 dark:divide-gray-700">
          {/* Null exposure means the flow was not calculated, so there is no cost to project. */}
          <ImpactFigure
            testKey="exposure-7-day"
            label={`${EXPOSURE_DAYS.SHORT}-Day Exposure`}
            value={exposure7Day == null ? '—' : formatCurrency(exposure7Day)}
          />
          <ImpactFigure
            testKey="exposure-30-day"
            label={`${EXPOSURE_DAYS.LONG}-Day Exposure`}
            value={exposure30Day == null ? '—' : formatCurrency(exposure30Day)}
          />
          <ImpactFigure
            testKey="cost-rank"
            label="Cost Rank In Portfolio"
            /*
              Ranked by configured cost impact, not by what the flow is costing today —
              which would put every flow that is fine into a tie at the back. The API
              returns both; dailyRiskCostRank is the other one.
            */
            value={costImpactRank ? `#${costImpactRank} of ${portfolioFlowCount}` : '—'}
          />
        </div>
      </div>

      <hr className="border-gray-300 dark:border-gray-700" />

      <div className="p-5">
        <TestableText dataTestId="bv-flow-categories-title" className="uppercase tracking-wide">
          Risk Categories
        </TestableText>
        <div className="mt-3 flex flex-wrap gap-2">
          {riskCategories.length === 0 && (
            <TestableText dataTestId="bv-flow-no-categories" className="text-gray-700 dark:text-gray-300">
              None configured.
            </TestableText>
          )}
          {riskCategories.map((category) => (
            <span
              key={category}
              data-testid={`bv-flow-category-${category}`}
              className="inline-flex items-center rounded-md border border-gray-300 px-2 py-0.5 text-sm text-gray-700 dark:border-gray-600 dark:text-gray-300"
            >
              {category}
            </span>
          ))}
        </div>
      </div>
    </TestableCard>
  );
};

BusinessViewFlowImpactCard.propTypes = {
  workflow: PropTypes.shape({
    riskStatus: PropTypes.string,
    riskCategories: PropTypes.arrayOf(PropTypes.string),
    costImpactRank: PropTypes.number,
    portfolioFlowCount: PropTypes.number,
  }).isRequired,
  nonPassingCount: PropTypes.number.isRequired,
  dailyCost: PropTypes.number,
  hasCurrentRisk: PropTypes.bool,
  exposure7Day: PropTypes.number,
  exposure30Day: PropTypes.number,
};

export default BusinessViewFlowImpactCard;
